import { getProps } from "@/utils/helpers/columnsHelper";
import {
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  FunctionField,
  CreateButton,
  TopToolbar,
  ListContextProvider,
  useListController,
  SelectColumnsButton,
  ExportButton,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { ResourceName, ReimbursementCreate, Reimbursement, Policy } from "@/types";
import { PolicyField } from "@/components/common/PolicyField";
import { useState } from "react";
import { ReimbursementListAside } from "./components/ReimbursementListAside";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { defaultCurrencyFormatterOptions } from "@/utils/formatters/currencyFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { isNonProductionEnvironment } from "@/utils/environment";
import { Download as DownloadIcon } from "@mui/icons-material";

export const ReimbursementList = () => {
  // Explicit default sort preserves the prior server order (received_date, newest
  // first) now that the API honors sort. Without it react-admin would default to
  // id ASC.
  const listContext = useListController({
    resource: ResourceName.REIMBURSEMENTS,
    sort: { field: "received_date", order: "DESC" },
  });

  const { isPending: loading } = listContext;

  const ReimbursementsIcon = getResourceIcon(ResourceName.REIMBURSEMENTS);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Reimbursements"
        hideSectionTitle
        rightContent={<ReimbursementsListActions loading={loading} />}
        headerIcon={<ReimbursementsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List
        actions={false}
        sort={{ field: "received_date", order: "DESC" }}
        aside={<ReimbursementListAside />}
        loading={loading}
        empty={<ListEmptyState icon={ReimbursementsIcon} resource={ResourceName.REIMBURSEMENTS} />}
      >
        <StyledDatagridConfigurable
          rowClick="show"
          bulkActionButtons={false}
          omit={[
            "aegle_id",
            "claim_id_ref",
            "policy_id_ref",
            "service_end_date",
            "check_date",
            "received_date",
            "adjustment_amount",
            "pended_amount",
            "denial_amount",
          ]}
        >
          {/* Primary identifier: Claimant name */}
          <ReferenceField
            {...getProps("claimant_id_ref", ResourceName.REIMBURSEMENTS)}
            sortable={false}
            reference={ResourceName.CLAIMANTS}
            link="show"
          >
            <TextField source="claimant_name" />
          </ReferenceField>

          {/* Client */}
          <ReferenceField
            {...getProps("client_id_ref", ResourceName.REIMBURSEMENTS)}
            sortable={false}
            reference={ResourceName.CLIENTS}
            link="show"
          >
            <TextField source="client_legal_name" />
          </ReferenceField>

          {/* Key financial amounts */}
          <NumberField
            {...getProps("requested_amount", ResourceName.REIMBURSEMENTS)}
            options={defaultCurrencyFormatterOptions}
          />
          <NumberField
            {...getProps("reimbursement_amount", ResourceName.REIMBURSEMENTS)}
            options={defaultCurrencyFormatterOptions}
          />

          {/* Status */}
          <StatusChipField {...getProps("status", ResourceName.REIMBURSEMENTS)} />

          {/* Service date */}
          <DateField {...getProps("service_start_date", ResourceName.REIMBURSEMENTS)} />

          {/* Payment method */}
          <FunctionField
            {...getProps("payment_method", ResourceName.REIMBURSEMENTS)}
            sortable={false}
            render={(record: Reimbursement) =>
              record.eft ? "EFT" : record.check_number ? `Check #${record.check_number}` : "-"
            }
          />

          {/* Hidden by default — user can toggle on */}
          <TextField {...getProps("aegle_id", ResourceName.REIMBURSEMENTS)} />
          <ReferenceField
            {...getProps("claim_id_ref", ResourceName.REIMBURSEMENTS)}
            sortable={false}
            reference={ResourceName.STOPLOSS_CLAIMS}
            link="show"
          >
            <TextField source="aegle_id" />
          </ReferenceField>
          <ReferenceField
            {...getProps("policy_id_ref", ResourceName.REIMBURSEMENTS)}
            sortable={false}
            reference={ResourceName.POLICIES}
            link={false}
          >
            <FunctionField render={(policy: Reimbursement) => <PolicyField policy={policy as unknown as Policy} />} />
          </ReferenceField>
          <DateField {...getProps("service_end_date", ResourceName.REIMBURSEMENTS)} />
          <DateField {...getProps("check_date", ResourceName.REIMBURSEMENTS)} />
          <DateField {...getProps("received_date", ResourceName.REIMBURSEMENTS)} />
          <NumberField
            {...getProps("adjustment_amount", ResourceName.REIMBURSEMENTS)}
            options={defaultCurrencyFormatterOptions}
          />
          <NumberField
            {...getProps("pended_amount", ResourceName.REIMBURSEMENTS)}
            options={defaultCurrencyFormatterOptions}
          />
          <NumberField
            {...getProps("denial_amount", ResourceName.REIMBURSEMENTS)}
            options={defaultCurrencyFormatterOptions}
          />
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};

// Top toolbar actions
const ReimbursementsListActions = ({ loading }: { loading?: boolean }) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.REIMBURSEMENTS);

  return (
    <TopToolbar>
      {canCreate && (
        <>
          <CreateButton
            onClick={(e) => {
              e.preventDefault();
              setOpenCreateDialog(true);
            }}
            disabled={loading}
          />
          <CreateInDialog<ReimbursementCreate>
            open={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            resource={ResourceName.REIMBURSEMENTS}
          />
        </>
      )}
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.REIMBURSEMENTS)}
          disabled={loading}
        />
      )}
    </TopToolbar>
  );
};
